/**
 * SPLIT-BAND THICKNESS (2026-10-02, Matt: "painting the finish on a split wall should
 * allow each portion of the wall to be its appropriate thickness — right now it appears
 * to adopt only one thickness across the wall plane").
 *
 * The kernel (`wallFaceSplits.ts#wallBandSteps`) reports, per banded face, each band's
 * thickness change against the face (`el.faceBandSteps`). Here:
 *   - the wall BODY builds to the THINNEST band on each side (`recessForBandSteps` —
 *     outer finish layers thinned, never the core; the core stays put through the
 *     painted shift, the corrugated recess's mechanism);
 *   - every band thicker than that gets a STRIP from the recessed face out to its own
 *     real face, in its own material — so a stone base stands proud of the stucco
 *     above it with a real ledge.
 *
 * STRIPS FROM SKINS (2026-10-04, Matt: lines showed at every window jamb, and the strip
 * stopped short of a wall standing on a roof): a strip is the band's own FINISH SKIN
 * thickened. The skins already follow every face the wall has — the body, header/sill
 * pieces, attach and footing caps, corner-weld notches — cut at the band lines. So the
 * strips are built by a pass AFTER the corner welds (`bandStripSolids`, from pipeline
 * syntheticSolids): it collects each banded face's skin triangles, merges every flat
 * plane into ONE outline across walls (union — collinear walls share one strip, no
 * seam), thickens that, and runs the first of two stepped planes on across an outside
 * corner so the corner block is closed. A corrugated face is never recessed (its sheet
 * already owns that zone) — only proud bands are added there.
 */
import { faceBands, type Pt2, type ResolvedElement } from '@aj/bim-kernel'
import earcut from 'earcut'
import { newMeshAcc, meshAccUvs, pushTri, type MeshAcc } from '../roof/meshAcc'
import { unionClosed, unionClosedGaps, signedArea } from '../../clipper'
import type { ElementSolid } from './generate3d'

type Side = 'exterior' | 'interior'
type Asm = NonNullable<ResolvedElement['assembly']> & { paintShiftFt?: number; paintedFrom?: unknown }

/** how far each side's body face is pulled in (feet) */
export function recessBySide(el: ResolvedElement): Record<Side, number> {
  const r = { exterior: 0, interior: 0 }
  const st = el.faceBandSteps
  if (!st) return r
  for (const side of ['exterior', 'interior'] as Side[]) {
    const steps = st[side]
    if (!steps || el.claddingSpecs?.[side]?.spec.profile === 'corrugated') continue
    const min = Math.min(0, ...steps.map((s) => s.deltaFt))
    r[side] = -min
  }
  return r
}

/** the element the wall body builds from: each side thinned to its thinnest band */
export function recessForBandSteps(el: ResolvedElement): ResolvedElement {
  const rec = recessBySide(el)
  const asm = el.assembly as Asm | null
  if (!asm || (!rec.exterior && !rec.interior)) return el
  const layers = asm.layers.map((l) => ({ ...l }))
  const core = asm.coreBoundary
  let shift = asm.paintShiftFt || 0
  for (const side of ['exterior', 'interior'] as Side[]) {
    let cut = rec[side]
    if (!cut) continue
    // walk inward from the outer face through the non-core layers
    const order = side === 'exterior'
      ? layers.map((_, i) => i).filter((i) => i < core.start)
      : layers.map((_, i) => i).filter((i) => i > core.end).reverse()
    let took = 0
    for (const i of order) {
      if (cut <= 1e-9) break
      const t = Math.min(cut, Math.max(0, layers[i].thicknessFt - 1 / 384))
      layers[i] = { ...layers[i], thicknessFt: layers[i].thicknessFt - t }
      cut -= t; took += t
    }
    shift += side === 'exterior' ? -took / 2 : took / 2
  }
  const assembly = { ...asm, layers, paintShiftFt: shift, paintedFrom: asm.paintedFrom || asm }
  return { ...el, assembly, assemblyThicknessFt: layers.reduce((s, l) => s + l.thicknessFt, 0) }
}

type V3 = [number, number, number]

export interface BandedWall {
  el: ResolvedElement
  /** the body's (recessed) thickness — how far a face can sit from the stored path */
  T: number
  nodes: Pt2[]
  sides: { side: Side; splitZ: number[]; step: (z: number) => { step: number; mat: string } | null }[]
}

export function bandedWall(el: ResolvedElement): BandedWall | null {
  const steps = el.faceBandSteps
  if (!steps) return null
  const body = recessForBandSteps(el)
  const rec = recessBySide(el)
  const asm = body.assembly
  const path = el.params.path as { nodes: { pt: Pt2 }[] } | null
  if (!asm || !path?.nodes || path.nodes.length < 2) return null
  const layers = asm.layers
  const zWallBase = (el.level?.elevationFt ?? 0) + ((el.params.baseOffsetFt as number) || 0)
  const sides: BandedWall['sides'] = []
  for (const side of ['exterior', 'interior'] as Side[]) {
    const st = steps[side]
    if (!st) continue
    const own = (side === 'exterior' ? layers[0] : layers[layers.length - 1])?.materialId as string | undefined
    const splitZ = faceBands(el.params, side).filter((b) => Number.isFinite(b.z0)).map((b) => zWallBase + b.z0)
    sides.push({
      side,
      splitZ,
      step: (z: number) => {
        for (const b of st) {
          if (z >= zWallBase + b.z0 - 1e-6 && z <= zWallBase + b.z1 + 1e-6) {
            const step = b.deltaFt + rec[side]
            const mat = b.materialId || own
            return step > 1 / 384 && mat ? { step, mat } : null
          }
        }
        return null
      },
    })
  }
  return sides.length ? { el, T: body.assemblyThicknessFt, nodes: path.nodes.map((n) => n.pt), sides } : null
}

/** the side of the wall a skin triangle (moved back onto the body face) lies on */
export function sideOfTriangle(w: BandedWall, n: [number, number], cx: number, cy: number): Side | null {
  let best: { dist: number; left: [number, number] } | null = null
  for (let i = 0; i + 1 < w.nodes.length; i++) {
    const A = w.nodes[i], B = w.nodes[i + 1]
    const L = Math.hypot(B[0] - A[0], B[1] - A[1])
    if (L < 1e-6) continue
    const u: [number, number] = [(B[0] - A[0]) / L, (B[1] - A[1]) / L]
    const left: [number, number] = [-u[1], u[0]]
    if (Math.abs(left[0] * n[0] + left[1] * n[1]) < 0.99) continue   // not this run's face
    // a corner-weld notch sits just past the wall's own end
    const s = (cx - A[0]) * u[0] + (cy - A[1]) * u[1]
    if (s < -w.T - 1 || s > L + w.T + 1) continue
    const dist = Math.abs((cx - A[0]) * left[0] + (cy - A[1]) * left[1])
    if (!best || dist < best.dist) best = { dist, left }
  }
  if (!best || best.dist > w.T + 0.05) return null
  const leftIsExt = w.el.params.flipped !== true
  const facesLeft = best.left[0] * n[0] + best.left[1] * n[1] > 0
  return facesLeft === leftIsExt ? 'exterior' : 'interior'
}

export interface StepFace {
  /** the inner (recessed body) face plane: unit horizontal normal + offset */
  n: [number, number]
  d: number
  step: number
  mat: string
  /** world z of band splits inside the wall — the ledge's inner corner line draws there */
  splitZ: number[]
  ids: Set<string>
  levelId: string
  polys: Pt2[][]
}

const SKIN_NUDGE_FT = 0.01   // generate3d WALL_SKIN_EPS_FT — skins ride this far proud

/**
 * The stepped-band strips for every banded wall in the scene, built from the finish
 * skins (each wall's own + the corner-weld notches') — see the file header.
 */
export function bandStripSolids(solids: ElementSolid[], byId: Map<string, ResolvedElement>): ElementSolid[] {
  const banded = new Map<string, BandedWall>()
  for (const el of byId.values()) {
    if (el.category !== 'wall' || !el.faceBandSteps) continue
    const w = bandedWall(el)
    if (w) banded.set(el.id, w)
  }
  if (!banded.size) return []
  const faces = new Map<string, StepFace>()
  for (const s of solids) {
    if (!s.skin || !s.mesh) continue
    const ids = (s.contributorIds ?? [s.elementId]).filter((id) => banded.has(id))
    if (!ids.length) continue
    const P = s.mesh.positions, N = s.mesh.normals, I = s.mesh.indices
    for (let t = 0; t + 2 < I.length; t += 3) {
      const ia = I[t], ib = I[t + 1], ic = I[t + 2]
      const nx = N[ia * 3], ny = N[ia * 3 + 1]
      const hl = Math.hypot(nx, ny)
      if (hl < 0.9) continue
      const n: [number, number] = [nx / hl, ny / hl]
      // back onto the body face the skin was nudged off
      const pts: V3[] = [ia, ib, ic].map((k) => [P[k * 3] - n[0] * SKIN_NUDGE_FT, P[k * 3 + 1] - n[1] * SKIN_NUDGE_FT, P[k * 3 + 2]] as V3)
      const cx = (pts[0][0] + pts[1][0] + pts[2][0]) / 3, cy = (pts[0][1] + pts[1][1] + pts[2][1]) / 3
      const zc = (pts[0][2] + pts[1][2] + pts[2][2]) / 3
      let hit: { step: number; mat: string; splitZ: number[]; id: string; levelId: string } | null = null
      for (const id of ids) {
        const w = banded.get(id)!
        const side = sideOfTriangle(w, n, cx, cy)
        if (!side) continue
        const sd = w.sides.find((x) => x.side === side)
        const st = sd?.step(zc)
        if (sd && st) hit = { ...st, splitZ: sd.splitZ, id, levelId: w.el.levelId as string }
        break
      }
      if (!hit) continue
      const d = n[0] * pts[0][0] + n[1] * pts[0][1]
      // one strip per plane + finish + step, matched by TOLERANCE (a rounded key split
      // one face in two wherever float noise straddled a rounding boundary)
      let f: StepFace | undefined
      for (const g of faces.values()) {
        if (g.mat === hit.mat && Math.abs(g.step - hit.step) < 1e-4 && g.n[0] * n[0] + g.n[1] * n[1] > 0.99995 && Math.abs(g.d - d) < 0.005) { f = g; break }
      }
      if (!f) {
        f = { n, d, step: hit.step, mat: hit.mat, splitZ: [], ids: new Set(), levelId: hit.levelId, polys: [] }
        faces.set(`${n[0].toFixed(3)},${n[1].toFixed(3)},${d.toFixed(3)}|${hit.mat}|${hit.step.toFixed(4)}|${faces.size}`, f)
      }
      f.ids.add(hit.id)
      for (const z of hit.splitZ) if (!f.splitZ.some((q) => Math.abs(q - z) < 1e-4)) f.splitZ.push(z)
      const u: [number, number] = [-n[1], n[0]]
      const tri = pts.map((p) => [p[0] * u[0] + p[1] * u[1], p[2]] as Pt2)
      if (Math.abs(signedArea(tri)) > 1e-7) f.polys.push(tri)
    }
  }
  if (!faces.size) return []
  // each plane's patches → its outline rings
  // (closing gaps under ~1/8": a face cut around an opening comes back as patches a
  // hair apart, which a plain union leaves as separate strips with walls between)
  const regions = [...faces.entries()].map(([key, f]) => ({ key, f, rings: unionClosedGaps(f.polys, 0.005) }))
  // OUTSIDE CORNERS: the first of two stepped planes (by key) runs on across the block
  for (const P of regions) {
    for (const Q of regions) {
      if (P === Q || P.key > Q.key) continue
      const ext = cornerExtension(P.f, P.rings, Q.f, Q.rings)
      if (ext.length) P.rings = unionClosed([...P.rings, ...ext])
    }
  }
  const out: ElementSolid[] = []
  for (const { key, f, rings } of regions) {
    const acc = newMeshAcc()
    const edges: number[] = []
    const front: number[] = []
    buildStrip(acc, edges, f, rings, front)
    if (!acc.vc) continue
    let zMin = Infinity, zMax = -Infinity
    for (let i = 2; i < acc.pos.length; i += 3) { zMin = Math.min(zMin, acc.pos[i]); zMax = Math.max(zMax, acc.pos[i]) }
    const uv = meshAccUvs(acc)
    const ids = [...f.ids]
    out.push({
      elementId: ids[0], ...(ids.length > 1 ? { contributorIds: ids } : {}),
      category: 'wall', outlines: [], zBase: zMin, zTop: zMax, materialId: f.mat,
      groupKey: `wall|${f.levelId}|band|${key}`,
      pieces: [], placements: [], openingInstances: [],
      mesh: {
        positions: new Float32Array(acc.pos), normals: new Float32Array(acc.nrm), indices: new Uint32Array(acc.idx), edges,
        ...(uv ? { uvs: uv } : {}),
      },
      meshOnly: true, skipProjectedOutline: true,
      // a decoration piece of its wall (the cladding precedent): plan's per-wall pass and
      // the corner welds never read it as the wall itself
      skirt: true,
      finishFace: finishFaceOf(front, f.n),
    } as ElementSolid)
  }
  return out
}

/** a flat list of triangle corners (x,y,z ×3 per triangle) facing horizontal `n` → a
 *  FINISH FACE mesh (ElementSolid.finishFace) */
export function finishFaceOf(tri: number[], n: [number, number]): NonNullable<ElementSolid['finishFace']> {
  const vc = tri.length / 3
  const nrm = new Float32Array(vc * 3)
  for (let i = 0; i < vc; i++) { nrm[i * 3] = n[0]; nrm[i * 3 + 1] = n[1] }
  const idx = new Uint32Array(vc)
  for (let i = 0; i < vc; i++) idx[i] = i
  return { positions: new Float32Array(tri), normals: nrm, indices: idx }
}

function ringsBox(rings: Pt2[][]): [number, number, number, number] {
  let s0 = Infinity, s1 = -Infinity, z0 = Infinity, z1 = -Infinity
  for (const r of rings) for (const p of r) { s0 = Math.min(s0, p[0]); s1 = Math.max(s1, p[0]); z0 = Math.min(z0, p[1]); z1 = Math.max(z1, p[1]) }
  return [s0, s1, z0, z1]
}

/**
 * The corner block P runs on across, where P's and Q's planes meet at an OUTSIDE
 * corner: a rectangle in P's (s, z), Q's step long, over the z range both strips share.
 * Empty when the planes don't meet at a convex corner both regions end at.
 */
function cornerExtension(P: StepFace, Pr: Pt2[][], Q: StepFace, Qr: Pt2[][]): Pt2[][] {
  const det = P.n[0] * Q.n[1] - P.n[1] * Q.n[0]
  if (Math.abs(det) < 0.5) return []
  const X: Pt2 = [(P.d * Q.n[1] - Q.d * P.n[1]) / det, (P.n[0] * Q.d - Q.n[0] * P.d) / det]
  const uP: Pt2 = [-P.n[1], P.n[0]], uQ: Pt2 = [-Q.n[1], Q.n[0]]
  const sP = X[0] * uP[0] + X[1] * uP[1], sQ = X[0] * uQ[0] + X[1] * uQ[1]
  const bP = ringsBox(Pr), bQ = ringsBox(Qr)
  const endsAt = (b: number[], s: number) => Math.abs(b[0] - s) < 0.05 || Math.abs(b[1] - s) < 0.05
  if (!endsAt(bP, sP) || !endsAt(bQ, sQ)) return []
  // convex: running on past P's end goes the way Q faces
  const outDir = Math.abs(bP[1] - sP) < Math.abs(bP[0] - sP) ? 1 : -1
  if (uP[0] * outDir * Q.n[0] + uP[1] * outDir * Q.n[1] < 0.5) return []
  // and Q's region likewise ends running the way P faces
  const qOut = Math.abs(bQ[1] - sQ) < Math.abs(bQ[0] - sQ) ? 1 : -1
  if (uQ[0] * qOut * P.n[0] + uQ[1] * qOut * P.n[1] < 0.5) return []
  const z0 = Math.max(bP[2], bQ[2]), z1 = Math.min(bP[3], bQ[3])
  if (z1 - z0 < 1e-3) return []
  const sB = sP + outDir * Q.step
  return [[[Math.min(sP, sB), z0], [Math.max(sP, sB), z0], [Math.max(sP, sB), z1], [Math.min(sP, sB), z1]]]
}

/** thicken one plane's rings into its strip: front face, side walls, edges */
export function buildStrip(
  acc: MeshAcc, edges: number[], f: Pick<StepFace, 'n' | 'd' | 'step' | 'splitZ'>, rings: Pt2[][],
  /** the front face's triangles, collected (a FINISH FACE for the surface-detail pass) */
  frontOut?: number[],
): void {
  const n = f.n
  const u: Pt2 = [-n[1], n[0]]
  const at = (s: number, z: number, off: number): number[] => [u[0] * s + n[0] * (f.d + off), u[1] * s + n[1] * (f.d + off), z]
  const outers = rings.filter((r) => signedArea(r) > 0)
  const holes = rings.filter((r) => signedArea(r) < 0)
  for (const outer of outers) {
    const mine = holes.filter((h) => pointInRing(h[0], outer))
    const flat: number[] = []
    const holeIdx: number[] = []
    for (const p of outer) flat.push(p[0], p[1])
    for (const h of mine) { holeIdx.push(flat.length / 2); for (const p of h) flat.push(p[0], p[1]) }
    const tri = earcut(flat, holeIdx)
    const front = (k: number) => at(flat[k * 2], flat[k * 2 + 1], f.step)
    for (let i = 0; i < tri.length; i += 3) {
      const a = front(tri[i]), b = front(tri[i + 1]), c = front(tri[i + 2])
      const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2], vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
      const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz
      if (nx * n[0] + ny * n[1] >= 0) pushTri(acc, a, b, c)
      else pushTri(acc, a, c, b)
      frontOut?.push(...a, ...b, ...c)
    }
  }
  // side walls + lines: every ring keeps its region on the LEFT (outer CCW, holes CW),
  // so the side wall's outward side is the right of each ring edge
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const p = ring[i], q = ring[(i + 1) % ring.length]
      if (Math.hypot(q[0] - p[0], q[1] - p[1]) < 1e-6) continue
      const out2: Pt2 = [q[1] - p[1], -(q[0] - p[0])]
      const outW = [u[0] * out2[0], u[1] * out2[0], out2[1]]
      const a0 = at(p[0], p[1], 0), b0 = at(q[0], q[1], 0), b1 = at(q[0], q[1], f.step), a1 = at(p[0], p[1], f.step)
      const ux = b0[0] - a0[0], uy = b0[1] - a0[1], uz = b0[2] - a0[2], vx = a1[0] - a0[0], vy = a1[1] - a0[1], vz = a1[2] - a0[2]
      const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
      if (nx * outW[0] + ny * outW[1] + nz * outW[2] >= 0) { pushTri(acc, a0, b0, b1); pushTri(acc, a0, b1, a1) }
      else { pushTri(acc, a0, b1, b0); pushTri(acc, a0, a1, b1) }
      // the strip's own outline on its face (the seam pass drops any that turn out to
      // continue a same-finish surface)
      edges.push(...a1, ...b1)
      // where a ledge meets the recessed face of the next band — elsewhere the side
      // wall continues the body's own jamb / end / top face and draws nothing
      if (Math.abs(p[1] - q[1]) < 1e-5 && f.splitZ.some((z) => Math.abs(z - p[1]) < 1e-3)) edges.push(...a0, ...b0)
    }
  }
}

function pointInRing(p: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i], b = ring[j]
    if ((a[1] > p[1]) !== (b[1] > p[1]) && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0]) inside = !inside
  }
  return inside
}

/**
 * LAP SIDING (2026-10-05): `buildStrip` with a VARIABLE front — each front vertex stands
 * `off(s, z)` proud of the face (a lap course: 0 at its top, the kick at its butt) and may
 * slide along the face by `slide(s, z)` (a mitered corner: the course runs past the
 * corner by its own depth so two faces meet on the bisector). The front stays planar for
 * an off() linear in z. Side walls are built only where the front stands off the face.
 */
export function buildStripVar(
  acc: MeshAcc, edges: number[], f: Pick<StepFace, 'n' | 'd'>, rings: Pt2[][],
  off: (s: number, z: number) => number,
  slide: (s: number, z: number) => number = () => 0,
): void {
  const n = f.n
  const u: Pt2 = [-n[1], n[0]]
  const at = (s: number, z: number, o: number): number[] => [u[0] * s + n[0] * (f.d + o), u[1] * s + n[1] * (f.d + o), z]
  const front = (s: number, z: number): number[] => at(s + slide(s, z), z, off(s, z))
  const EPS = 1e-6
  const outers = rings.filter((r) => signedArea(r) > 0)
  const holes = rings.filter((r) => signedArea(r) < 0)
  for (const outer of outers) {
    const mine = holes.filter((h) => pointInRing(h[0], outer))
    const flat: number[] = []
    const holeIdx: number[] = []
    for (const p of outer) flat.push(p[0], p[1])
    for (const h of mine) { holeIdx.push(flat.length / 2); for (const p of h) flat.push(p[0], p[1]) }
    const tri = earcut(flat, holeIdx)
    const F = (k: number) => front(flat[k * 2], flat[k * 2 + 1])
    for (let i = 0; i < tri.length; i += 3) {
      const a = F(tri[i]), b = F(tri[i + 1]), c = F(tri[i + 2])
      const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2], vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2]
      const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
      if (Math.hypot(nx, ny, nz) < 1e-12) continue
      if (nx * n[0] + ny * n[1] >= 0) pushTri(acc, a, b, c)
      else pushTri(acc, a, c, b)
    }
  }
  for (const ring of rings) {
    for (let i = 0; i < ring.length; i++) {
      const p = ring[i], q = ring[(i + 1) % ring.length]
      if (Math.hypot(q[0] - p[0], q[1] - p[1]) < 1e-6) continue
      const op = off(p[0], p[1]), oq = off(q[0], q[1])
      if (op < EPS && oq < EPS) continue
      const out2: Pt2 = [q[1] - p[1], -(q[0] - p[0])]
      const outW = [u[0] * out2[0], u[1] * out2[0], out2[1]]
      const a0 = at(p[0], p[1], 0), b0 = at(q[0], q[1], 0), b1 = front(q[0], q[1]), a1 = front(p[0], p[1])
      const tris: number[][][] = []
      if (op < EPS) tris.push([a0, b0, b1])
      else if (oq < EPS) tris.push([a0, b0, a1])
      else tris.push([a0, b0, b1], [a0, b1, a1])
      for (const [A, B, C] of tris) {
        const ux = B[0] - A[0], uy = B[1] - A[1], uz = B[2] - A[2], vx = C[0] - A[0], vy = C[1] - A[1], vz = C[2] - A[2]
        const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx
        if (Math.hypot(nx, ny, nz) < 1e-12) continue
        if (nx * outW[0] + ny * outW[1] + nz * outW[2] >= 0) pushTri(acc, A, B, C)
        else pushTri(acc, A, C, B)
      }
      edges.push(...a1, ...b1)
    }
  }
}
